// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { diaHoraCorta, hoyAr } from "../shared/fechaAr";
import { PESTANAS, diasAtras } from "../shared/periodoVales";
import { controlDeSecuencia } from "../shared/talonario";
import {
  AGRUPACIONES,
  choferDe,
  esPatenteLibre,
  filtrar,
  numerosDe,
  totales,
  unidadDe,
} from "../shared/gasoil";
import { aExcel } from "../services/exportarGasoil";
import {
  FaltaMigracion,
  anularValesGasoil,
  borrarCargaGasoil,
  editarValeGasoil,
  revertirValeGasoil,
  traerAnuladosGasoil,
  traerCargasGasoil,
} from "../services/gasoil";
import FotoPrivada from "../components/funcionales/FotoPrivada";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import "./css/urea.css";

/* Gasoil de la base: los vales del surtidor propio, talonario «GAS OIL».
 *
 * Nico, 07/10/2026: «un lugar como el de los vales de Urea pero para el
 * gasoil cargado en la base». Es la pantalla de «Cargas de urea» con lo que
 * el gasoil necesita y la urea no:
 *
 *   · EL CHOFER Y LA UNIDAD, NO SÓLO EL INTERNO. El vale de gasoil se le
 *     llena a quien venga —un tractor, un utilitario, un auto— y lo que el
 *     papel trae es SEÑOR y PATENTE. Se muestra lo del papel y, si el bot lo
 *     pudo resolver sin dudas contra el padrón, el interno y el chofer del
 *     padrón al lado.
 *   · LOS TOTALES por día, mes, chofer o unidad, sobre lo que está filtrado.
 *
 * Y lo que comparte con la urea, sin copiarlo: el período (`periodoVales`),
 * el control de duplicados y huecos (`talonario.controlDeSecuencia`), la foto
 * (`FotoPrivada`, bucket `vales`, carpeta `gasoil/`), la fecha del Excel y los
 * estilos.
 *
 * ── MIRAR, BORRAR, CORREGIR EL NÚMERO Y ANULAR; NUNCA CARGAR ────────────────
 *
 * El alta es la foto del vale al bot. Acá, con `admin.borrar` se saca la carga
 * mal hecha, y con `editar.vale_gasoil` —los que CONTROLAN el talonario, nunca
 * los que lo cargan— se corrige el número leído mal y se anotan los números
 * anulados o sin usar, para que dejen de figurar como faltantes.
 */

const litrosTxt = (n) =>
  n == null || n === ""
    ? "—"
    : `${new Intl.NumberFormat("es-AR", { maximumFractionDigits: 2 }).format(n)} L`;

const NUMERICOS = new Set(["litros", "vale", "km"]);
const valorDeOrden = (c, campo) => {
  if (campo === "fecha") return c.fecha ? new Date(c.fecha).getTime() : null;
  if (campo === "chofer") return choferDe(c).toLowerCase() || null;
  if (campo === "unidad") return unidadDe(c).toLowerCase() || null;
  if (campo === "cargo")
    return (c.quien_nombre || c.quien || "").toLowerCase() || null;
  if (NUMERICOS.has(campo)) {
    const n = Number(c[campo]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const MOTIVOS = [
  { id: "anulado", label: "anulado (se escribió y se rompió)" },
  { id: "no_usado", label: "sin usar (nunca se llenó)" },
];

const Gasoil = () => {
  const { permisos = null } = useOutletContext() || {};
  const tiene = (p) => permisos !== null && permisos.includes(p);
  /* DOS LLAVES, CUALQUIERA DE LAS DOS, como la urea: `ver.gasoil` abre sólo
   * esto; `ver.combustible` ya ve el gasoil entero y no tiene por qué perder
   * el de la base. */
  const permitido = ["ver.combustible", "ver.gasoil"].some(tiene);
  const puedeBorrar = tiene("admin.borrar");
  const puedeCorregir = tiene("editar.vale_gasoil");

  const [pestana, setPestana] = useState("7");
  const [desde, setDesde] = useState(diasAtras(6));
  const [hasta, setHasta] = useState(hoyAr());
  const [datos, setDatos] = useState(undefined); // undefined = cargando
  const [error, setError] = useState("");        // "" | "base" | "migracion"
  const [anulados, setAnulados] = useState(null); // null = no se pudo leer
  const [estado, setEstado] = useState("");       // "" | ok:<msg> | mal:<msg>
  const [busqueda, setBusqueda] = useState("");
  const [agrupar, setAgrupar] = useState("dia");
  const [orden, setOrden] = useState({ campo: "fecha", dir: "desc" });

  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  const [anularTxt, setAnularTxt] = useState("");
  const [anularMotivo, setAnularMotivo] = useState("anulado");

  const cargar = () => {
    setDatos(undefined);
    setError("");
    traerCargasGasoil(desde, hasta)
      .then(setDatos)
      .catch((e) => setError(e instanceof FaltaMigracion ? "migracion" : "base"));
    traerAnuladosGasoil().then(setAnulados).catch(() => setAnulados(null));
  };

  useEffect(() => {
    if (!permitido) return;
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitido, desde, hasta]);

  const elegirPestana = (p) => {
    const [d, h] = p.rango();
    setPestana(p.id);
    setDesde(d);
    setHasta(h);
  };

  const visibles = useMemo(() => filtrar(datos || [], busqueda), [datos, busqueda]);

  const totalLitros = useMemo(
    () => visibles.reduce((s, c) => s + (Number(c.litros) || 0), 0),
    [visibles],
  );

  const grupos = useMemo(() => totales(visibles, agrupar), [visibles, agrupar]);

  /* Duplicados y huecos SOBRE TODO EL PERÍODO, no sobre lo filtrado: lo que
     falta en el talonario no depende de qué chofer se esté buscando. Los
     anulados salen de los faltantes; si no se pudieron leer, no sale ninguno
     (mostrar de más es mejor que esconder). */
  const secuencia = useMemo(
    () => controlDeSecuencia(datos || [], {
      anulados: (anulados || []).map((a) => a.numero),
    }),
    [datos, anulados],
  );

  const clickOrden = (campo) =>
    setOrden((o) =>
      o.campo === campo
        ? { campo, dir: o.dir === "asc" ? "desc" : "asc" }
        : { campo, dir: campo === "fecha" ? "desc" : "asc" },
    );

  const ordenadas = useMemo(() => {
    const arr = [...visibles];
    const { campo, dir } = orden;
    const s = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = valorDeOrden(a, campo);
      const vb = valorDeOrden(b, campo);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return -s;
      if (va > vb) return s;
      return 0;
    });
    return arr;
  }, [visibles, orden]);

  const th = (campo, label) => {
    const activa = orden.campo === campo;
    return (
      <th
        className={"urea-th-orden" + (activa ? " activa" : "")}
        onClick={() => clickOrden(campo)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            clickOrden(campo);
          }
        }}
        tabIndex={0}
        role="columnheader"
        aria-sort={activa ? (orden.dir === "asc" ? "ascending" : "descending") : "none"}
        title="Ordenar por esta columna"
      >
        {label}
        <span className="urea-flecha" aria-hidden="true">
          {activa ? (orden.dir === "asc" ? " ▲" : " ▼") : ""}
        </span>
      </th>
    );
  };

  const borrar = async (c) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `¿Borrar el vale ${c.vale || "sin número"} (${litrosTxt(c.litros)}` +
        `${choferDe(c) ? `, ${choferDe(c)}` : ""})? No hay papelera: si hacía ` +
        "falta, se vuelve a mandar la foto del vale al bot.",
    );
    if (!ok) return;
    try {
      await borrarCargaGasoil(c.id);
      setEstado("");
      cargar();
    } catch (e) {
      setEstado(`mal:${e?.message || "No se pudo borrar."}`);
    }
  };

  const guardarVale = async (c) => {
    setTrabajando(true);
    try {
      await editarValeGasoil(c.id, editVal);
      setEstado("");
      setEditId(null);
      cargar();
    } catch (e) {
      setEstado(`mal:${e?.message || "No se pudo corregir el vale."}`);
    } finally {
      setTrabajando(false);
    }
  };

  const anular = async () => {
    const nums = numerosDe(anularTxt);
    if (!nums.length) {
      setEstado("mal:Escribí los números a anular (por ejemplo «283281 a 283283»).");
      return;
    }
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `¿Marcar ${nums.length === 1 ? "el vale" : `${nums.length} vales`} ` +
        `(${nums.slice(0, 10).join(", ")}${nums.length > 10 ? "…" : ""}) como ` +
        `${anularMotivo === "anulado" ? "anulados" : "sin usar"}? Dejan de ` +
        "figurar como faltantes. Se puede deshacer.",
    );
    if (!ok) return;
    setTrabajando(true);
    try {
      const puestos = await anularValesGasoil(nums, anularMotivo);
      const fuera = nums.length - puestos.length;
      setEstado(
        `ok:Anotados ${puestos.length}.` +
          (fuera > 0
            ? ` ${fuera} no se tocaron: ya tenían carga o ya estaban anotados.`
            : ""),
      );
      setAnularTxt("");
      cargar();
    } catch (e) {
      setEstado(`mal:${e?.message || "No se pudo anular."}`);
    } finally {
      setTrabajando(false);
    }
  };

  const desanular = async (numero) => {
    setTrabajando(true);
    try {
      await revertirValeGasoil(numero);
      setEstado(`ok:El ${numero} vuelve a contarse como faltante si no aparece.`);
      cargar();
    } catch (e) {
      setEstado(`mal:${e?.message || "No se pudo deshacer."}`);
    } finally {
      setTrabajando(false);
    }
  };

  if (permisos !== null && !permitido) return <PuertaCerrada />;

  if (error === "migracion") {
    return (
      <div className="panel-aviso suave">
        <h3>Esta pantalla todavía no tiene dónde leer</h3>
        <p className="panel-aviso__chico">
          Falta crear la tabla de los vales de gasoil de la base. Cuando esté,
          los vales que se manden con foto al bot aparecen acá.
        </p>
      </div>
    );
  }
  if (error) {
    return <div className="panel-aviso"><h3>No se pudo consultar</h3></div>;
  }

  const hayFilas = datos !== undefined && visibles.length > 0;

  return (
    <>
      <div className="urea-barra">
        <div className="urea-periodos" role="group" aria-label="Período">
          {PESTANAS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={"urea-seg" + (pestana === p.id ? " activo" : "")}
              onClick={() => elegirPestana(p)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="urea-rango">
          <input
            type="date"
            value={desde}
            max={hasta}
            onChange={(e) => { setPestana(""); setDesde(e.target.value); }}
            aria-label="Vales desde esta fecha"
          />
          <span className="urea-rango-sep">→</span>
          <input
            type="date"
            value={hasta}
            min={desde}
            onChange={(e) => { setPestana(""); setHasta(e.target.value); }}
            aria-label="Vales hasta esta fecha"
          />
        </div>
        <input
          type="search"
          className="gasoil-buscar"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Chofer, patente, interno o vale"
          aria-label="Buscar por chofer, patente, interno o vale"
        />
        {hayFilas && (
          <button
            type="button"
            className="urea-seg"
            onClick={() => aExcel(ordenadas, desde, hasta)}
          >
            Bajar Excel
          </button>
        )}
        {hayFilas && (
          <div className="urea-resumen">
            <span className="urea-cifra">{visibles.length}</span>
            <span className="urea-unidad">{visibles.length === 1 ? "vale" : "vales"}</span>
            <span className="urea-cifra">
              {new Intl.NumberFormat("es-AR", { maximumFractionDigits: 2 }).format(totalLitros)}
            </span>
            <span className="urea-unidad">litros</span>
          </div>
        )}
        {estado.startsWith("mal:") && <span className="pill mal">{estado.slice(4)}</span>}
        {estado.startsWith("ok:") && <span className="pill ok">{estado.slice(3)}</span>}
      </div>

      <p className="urea-ayuda-corregir">
        Los vales de <b>GAS OIL</b> del surtidor de la base entran mandándole la{" "}
        <b>foto del vale</b> al bot por WhatsApp, igual que los de urea: el bot
        reconoce el talonario, muestra lo que leyó y se guarda con un <b>SI</b>.
        Acá se miran, se suman y se baja el Excel.
      </p>

      {datos !== undefined && visibles.length === 0 && (
        <div className="panel-aviso suave">
          <h3>
            {datos.length === 0
              ? "Sin vales de gasoil en el período"
              : "Ningún vale coincide con la búsqueda"}
          </h3>
          {datos.length > 0 && (
            <p className="panel-aviso__chico">
              {`Hay ${datos.length} en el período.`}
            </p>
          )}
        </div>
      )}

      {(secuencia.duplicados.length > 0 || secuencia.faltantes.length > 0) && (
        <div className="urea-alerta">
          {secuencia.duplicados.length > 0 && (
            <p>
              <b>
                ⚠️ Talonario GAS OIL · Vale
                {secuencia.duplicados.length > 1 ? "s" : ""} cargado
                {secuencia.duplicados.length > 1 ? "s" : ""} más de una vez:
              </b>{" "}
              {secuencia.duplicados.join(", ")}. Fijate si uno es en realidad
              otro número.
            </p>
          )}
          {secuencia.faltantes.length > 0 && (
            <p className="urea-alerta__suave">
              Talonario GAS OIL · Faltan vales en la secuencia:{" "}
              {secuencia.faltantes.length <= 25
                ? secuencia.faltantes.join(", ")
                : `${secuencia.faltantes.length} números`}
              . Puede que estén sin cargar todavía, o salteados.
            </p>
          )}
        </div>
      )}

      {hayFilas && (
        <section className="gasoil-totales" aria-label="Totales">
          <div className="urea-barra">
            <span className="urea-unidad">Totales por</span>
            <div className="urea-periodos" role="group" aria-label="Agrupar por">
              {AGRUPACIONES.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={"urea-seg" + (agrupar === a.id ? " activo" : "")}
                  onClick={() => setAgrupar(a.id)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          <div className="tabla-wrap">
            <table className="tabla fija" aria-label={`Totales por ${agrupar}`}>
              <thead>
                <tr>
                  <th>{(AGRUPACIONES.find((a) => a.id === agrupar) || {}).label}</th>
                  <th className="num">Vales</th>
                  <th className="num">Litros</th>
                </tr>
              </thead>
              <tbody>
                {grupos.map((g) => (
                  <tr key={String(g.clave)}>
                    <td>{g.clave === null ? <span className="tenue">{g.rotulo}</span> : g.rotulo}</td>
                    <td className="num">{g.cargas}</td>
                    <td className="num">{litrosTxt(g.litros)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {hayFilas && (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                {th("fecha", "Cuándo")}
                {th("vale", "Vale")}
                {th("litros", "Litros")}
                {th("chofer", "Chofer")}
                {th("unidad", "Unidad")}
                <th>Referencia</th>
                {th("km", "Km")}
                {th("cargo", "Cargó")}
                <th>Vale (foto)</th>
                {puedeBorrar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {ordenadas.map((c) => (
                <tr key={c.id} data-ver={c.id}>
                  <td>{diaHoraCorta(c.fecha)}</td>
                  <td>
                    {editId === c.id ? (
                      <span className="urea-editvale">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={editVal}
                          onChange={(e) => setEditVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") guardarVale(c);
                            if (e.key === "Escape") setEditId(null);
                          }}
                          aria-label="Número de vale"
                          autoFocus
                          style={{ width: 90 }}
                        />
                        <button type="button" className="tk-boton tk-boton--chico"
                          disabled={trabajando} onClick={() => guardarVale(c)}>✓</button>
                        <button type="button" className="tk-boton tk-boton--chico tk-boton--flojo"
                          disabled={trabajando} onClick={() => setEditId(null)}>✕</button>
                      </span>
                    ) : (
                      <>
                        {c.vale || <span className="tenue">sin vale</span>}
                        {puedeCorregir && (
                          <button
                            type="button"
                            className="urea-lapiz"
                            title={c.vale_editado_por
                              ? `Corregido por ${c.vale_editado_por}`
                              : "Corregir el número de vale"}
                            aria-label="Corregir el número de vale"
                            onClick={() => { setEditId(c.id); setEditVal(c.vale || ""); }}
                          >✎</button>
                        )}
                        {c.vale_editado_por && (
                          <span className="tenue" title={`Corregido por ${c.vale_editado_por}`}> ·corregido</span>
                        )}
                      </>
                    )}
                  </td>
                  <td>{litrosTxt(c.litros)}</td>
                  <td>
                    {choferDe(c) || <span className="tenue">—</span>}
                    {c.chofer_padron && c.chofer && c.chofer_padron !== c.chofer && (
                      <span className="tenue" title="Lo que dice el vale"> ({c.chofer})</span>
                    )}
                  </td>
                  <td>
                    {unidadDe(c) || <span className="tenue">—</span>}
                    {esPatenteLibre(c) && (
                      <span className="tenue" title="La patente no está en el padrón de la flota"> · no es de la flota</span>
                    )}
                  </td>
                  <td>{c.referencia || <span className="tenue">—</span>}</td>
                  <td>
                    {c.km != null
                      ? `${new Intl.NumberFormat("es-AR").format(c.km)} km`
                      : <span className="tenue">—</span>}
                  </td>
                  <td>{c.quien_nombre || c.quien || <span className="tenue">—</span>}</td>
                  <td>
                    {c.foto ? (
                      <FotoPrivada
                        ruta={c.foto}
                        bucket="vales"
                        etiqueta="Ver foto"
                        titulo={`Vale de gasoil ${c.vale || "s/n"}`}
                      />
                    ) : (
                      <span className="tenue">sin foto</span>
                    )}
                  </td>
                  {puedeBorrar && (
                    <td className="agenda-acc">
                      <button
                        type="button"
                        className="tk-boton tk-boton--chico tk-boton--flojo"
                        onClick={() => borrar(c)}
                      >
                        Borrar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* LOS ANULADOS SE VEN SIEMPRE; anotarlos, sólo con la llave. Un número
          que desaparece de los faltantes sin que se vea por qué es un número
          escondido. */}
      {datos !== undefined && (puedeCorregir || (anulados || []).length > 0) && (
        <details className="gasoil-anulados">
          <summary>
            Vales anulados o sin usar
            {anulados ? ` (${anulados.length})` : ""}
          </summary>
          {anulados === null && (
            <p className="panel-aviso__chico">
              No se pudieron leer: los faltantes de arriba pueden traer de más.
            </p>
          )}
          {(anulados || []).length > 0 && (
            <ul>
              {anulados.map((a) => (
                <li key={a.id}>
                  <b>{a.numero}</b> · {a.motivo === "anulado" ? "anulado" : "sin usar"}
                  {a.quien ? <span className="tenue"> · {a.quien}</span> : null}
                  {puedeCorregir && (
                    <button
                      type="button"
                      className="tk-boton tk-boton--chico tk-boton--flojo"
                      disabled={trabajando}
                      onClick={() => desanular(a.numero)}
                    >
                      Deshacer
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {puedeCorregir && (
            <div className="gasoil-anular">
              <input
                type="text"
                value={anularTxt}
                onChange={(e) => setAnularTxt(e.target.value)}
                placeholder="283281 o 283281 a 283285"
                aria-label="Números de vale a anular"
              />
              <select
                value={anularMotivo}
                onChange={(e) => setAnularMotivo(e.target.value)}
                aria-label="Motivo"
              >
                {MOTIVOS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="tk-boton tk-boton--chico"
                disabled={trabajando}
                onClick={anular}
              >
                Anotar
              </button>
            </div>
          )}
        </details>
      )}
    </>
  );
};

export default Gasoil;
